import React, { useState, useEffect, useRef } from 'react'
import Select from 'react-select'
import { CAccordion, CAccordionItem, CAccordionHeader, CAccordionBody, CFormTextarea, CFormLabel, CButton, CNav, CNavItem, CNavLink, CModal, CModalHeader, CModalTitle, CModalBody, CModalFooter, CFormInput } from '@coreui/react'
import classNames from 'classnames'
import LabCRUD from '../../services/general/LabsAPI.api.js'
import MedicineCRUD from '../../services/general/Medication.api'
import ImagingInformation from '../../services/general/ImagingAPI.api.js'
import toast from 'react-hot-toast'
import { toastConfig } from '../../constants/other.config'
import SuggestionDiagnosis from './SuggestionDiagnosis.jsx'
import PlanSuggestions from './PlanSuggestions.jsx'
import AccordianFinal from '../../components/shared/AccordianFinal.jsx'
import CheckOptionSelections from '../../components/shared/CheckOptionSelections'

const AccordianSuggestion = ({
  suggestions,
  parentId,
  onSuggestionsChange,
  orders,
  setSaveResoanChange,
  selectedDiagnosis,
  handlePlanSuggestionsChange,
  planSuggestions,
  headerPlanSuggestions,
  onHeaderPlanAccordionsChange,
  item,
  showHeader = true,
  setOpenSelectedAccordian,
  openSelectedAccordian,
  checkTrue = false,
  setParentAccordions,
  information,
  setActiveDescriptions,
  handleAddStatementDescription,
  handleFileStatementData,
  handleStatementFileSave,
  file,
  setFile,
  fileStatmentData,
  attachedType,
  ApiCallingType,
  differentModuleCopyFunctionality,
  handelSubmit,
  deleteSuggestion,
  onEditOption,
}) => {
  const isSameJson = (firstValue, secondValue) => JSON.stringify(firstValue || []) === JSON.stringify(secondValue || [])
  const [expanded, setExpanded] = useState(null)
  const [selectedData, setSelectedData] = useState({})
  const [isPageLoading, setIsPageLoading] = useState(false)
  const [labList, setLabList] = useState([])
  const [medicationList, setMedicationList] = useState([])
  const [imagingList, setImagingList] = useState([])
  const [labOptions, setLabOptions] = useState([])
  const [medicationOptions, setMedicationOptions] = useState([])
  const [imagingOptions, setImagingOptions] = useState([])
  const [activeKey, setActiveKey] = useState(null)
  const [activeTab, setActiveTab] = useState('order')
  const [suggestionsData, setSuggestionsData] = useState(planSuggestions || [])
  const [selectedAccordian, setSelectedAccordian] = useState(null)
  const [activeAccordion, setActiveAccordion] = useState(null)

  useEffect(() => {
    const initialData = {}
    orders?.forEach((order) => {
      const suggestionName = order.name
      initialData[suggestionName] = {
        Lab: order?.data?.Lab || [],
        Medication: order?.data?.Medication || [],
        Imaging: order?.data?.Imaging || [],
      }
    })
    setSelectedData((prev) => (JSON.stringify(prev || {}) === JSON.stringify(initialData || {}) ? prev : initialData))
  }, [orders])

  useEffect(() => {
    setSuggestionsData((prev) => (isSameJson(prev, planSuggestions) ? prev : planSuggestions || []))
  }, [planSuggestions])

  useEffect(() => {
    setActiveTab('order')
  }, [selectedDiagnosis])

  const debounceTimeout = useRef(null)
  const debounce = (func, delay) => {
    clearTimeout(debounceTimeout.current)
    debounceTimeout.current = setTimeout(func, delay)
  }

  const triggerSaveSuggestions = (updatedData) => {
    const formattedSuggestions = suggestions.map((suggestion) => ({
      name: suggestion.name,
      data: updatedData[suggestion.name] || {},
    }))
    setSaveResoanChange({ formattedSuggestions, parentId })
    debounce(() => {
      onSuggestionsChange(formattedSuggestions, parentId)
    }, 300)
  }

  const handleMultiSelectChange = (suggestionName, type, selectedOptions) => {
    setSelectedData((prev) => {
      const updatedData = {
        ...prev,
        [suggestionName]: {
          ...prev[suggestionName],
          [type]: selectedOptions.map((option) => ({
            value: option,
            reason: prev[suggestionName]?.[type]?.find((item) => item.value.value === option.value)?.reason || '',
          })),
        },
      }

      triggerSaveSuggestions(updatedData)
      return updatedData
    })
  }

  const handleReasonChange = (suggestionName, type, value, index) => {
    setSelectedData((prev) => {
      const updatedData = {
        ...prev,
        [suggestionName]: {
          ...prev[suggestionName],
          [type]: prev[suggestionName][type].map((item, idx) => (idx === index ? { ...item, reason: value } : item)),
        },
      }
      triggerSaveSuggestions(updatedData)
      return updatedData
    })
  }

  const getLabData = async () => {
    setIsPageLoading(true)
    try {
      let labData = await LabCRUD('GETALL', '{}')
      labData = JSON.parse(labData.d)?.Data
      setLabList(labData)

      if (labData.length > 0) {
        let option = labData?.map((item) => ({
          value: item.ID,
          label: item.LabName,
        }))
        setLabOptions(option)
      }
      setIsPageLoading(false)
    } catch (error) {
      setIsPageLoading(false)
    }
  }

  const getMedicationData = async () => {
    setIsPageLoading(true)
    try {
      let allMedicines = await MedicineCRUD('GET')

      let formattedData
      formattedData = JSON.parse(allMedicines.d)?.Data
      formattedData = formattedData.map((item) => {
        return {
          ...item,
          Data: JSON.parse(item.Data),
        }
      })
      if (formattedData) {
        let medicationData = formattedData.map((item) => item.Data)
        setMedicationList(medicationData)

        if (medicationData.length > 0) {
          let option = medicationData?.map((item) => ({
            value: item.id,
            label: item.name,
          }))
          setMedicationOptions(option)
        }
      }
      setIsPageLoading(false)
    } catch (e) {
      console.error('Error fetching medication data', error)
      setIsPageLoading(false)
    }
  }

  const getImagingData = async () => {
    setIsPageLoading(true)
    try {
      const data = await ImagingInformation('GET_ALL')
      if (data) {
        const formattedData = JSON.parse(data.d)?.Data
        setImagingList(formattedData)
        if (formattedData.length > 0) {
          let option = formattedData?.map((item) => ({
            value: item.ID,
            label: item.Name,
          }))
          setImagingOptions(option)
        }
      }
    } catch (error) {
      console.error('Error fetching imaging data', error)
    } finally {
      setIsPageLoading(false)
    }
  }

  const handleSavePlanSuggestions = () => {
    try {
      handlePlanSuggestionsChange(suggestionsData, parentId)
      // toast.success('Successfully Plan Suggestions Added', toastConfig)
    } catch (err) {
      console.log(err, 'error on Plan Suggestion save')
    }
  }

  const handleAccordionsUpdate = (suggestionName, updatedAccordions) => {
    // let tempSuggestionData = suggestionsData
    // const existingIndex = tempSuggestionData.findIndex(item=>item.name == suggestionName)
    // if (existingIndex !== -1) {
    //     let updatedData = [...tempSuggestionData];
    //     updatedData[existingIndex] = {
    //         ...updatedData[existingIndex],
    //         accordions: updatedAccordions,
    //     };
    //     setSuggestionsData(updatedData);
    // } else {
    //     let extraSuggestion =  [
    //         ...tempSuggestionData,
    //         {
    //             name: suggestionName,
    //             accordions: updatedAccordions,
    //         },
    //     ];
    //     setSuggestionsData(extraSuggestion)
    // }
    setSuggestionsData((prevData) => {
      const existingIndex = prevData?.findIndex((item) => item.name === suggestionName)
      if (existingIndex !== -1) {
        const updatedData = [...prevData]
        updatedData[existingIndex] = {
          ...updatedData[existingIndex],
          accordions: updatedAccordions,
        }

        return updatedData
      } else {
        let tempData = [
          ...prevData,
          {
            name: suggestionName,
            accordions: updatedAccordions,
          },
        ]
        return tempData
      }
    })
  }

  useEffect(() => {
     if (onHeaderPlanAccordionsChange) return
    if (suggestionsData.length > 0) {
      debounce(() => {
        handleSavePlanSuggestions(suggestionsData)
      }, 300)
    }
  }, [suggestionsData])

  useEffect(() => {
    getLabData()
    getMedicationData()
    getImagingData()
  }, [])

  const toggleAccordion = (index, parentId, suggestion) => {
    setActiveAccordion(activeAccordion === index ? null : index)
    setOpenSelectedAccordian({ index, parentId, suggestion })
  }

  return (
    <div>
      {suggestions?.map((suggestion, index) => {
        if (checkTrue) {
          if (openSelectedAccordian && openSelectedAccordian?.parentId === parentId && openSelectedAccordian?.index === index) {
            return (
              <AccordianFinal
                title={suggestion.name}
                content={
                  <div className="assessment-plan-suggestion-theme__order-shell">
                    <div className="assessment-plan-suggestion-theme__order-tabs">
                      <CNav variant="pills" role="tablist" className="justify-content-center assessment-plan-suggestion-theme__order-nav">
                        <CNavItem>
                          <CNavLink active={activeTab === 'order'} onClick={() => setActiveTab('order')}>
                            Order
                          </CNavLink>
                        </CNavItem>
                        <CNavItem>
                          <CNavLink active={activeTab === 'plan'} onClick={() => setActiveTab('plan')}>
                            Plan Statement
                          </CNavLink>
                        </CNavItem>
                      </CNav>
                    </div>
                    {activeTab === 'order' && (
                      <div className="assessment-plan-suggestion-theme__order-panel">
                        <div className="assessment-plan-suggestion-theme__order-section">
                          <CheckOptionSelections options={labOptions} selectedValue={selectedData[suggestion.name]?.Lab?.map((item) => item.value) || []} setSelectedValue={(selectedOptions) => handleMultiSelectChange(suggestion.name, 'Lab', selectedOptions)} Label={'Lab'} />
                          {selectedData[suggestion.name]?.Lab?.map((lab, idx) => (
                            <div className="m-2 assessment-plan-suggestion-theme__order-reason" key={lab.value.value}>
                              <CFormTextarea placeholder={`Reason for ${lab.value.label}`} value={lab.reason || ''} onChange={(e) => handleReasonChange(suggestion.name, 'Lab', e.target.value, idx)} />
                            </div>
                          ))}
                        </div>
                        <div className="assessment-plan-suggestion-theme__order-section">
                          <CheckOptionSelections
                            options={medicationOptions}
                            selectedValue={selectedData[suggestion.name]?.Medication?.map((item) => item.value) || []}
                            setSelectedValue={(selectedOptions) => handleMultiSelectChange(suggestion.name, 'Medication', selectedOptions)}
                            Label={'Medication'}
                          />

                          {selectedData[suggestion.name]?.Medication?.map((medication, idx) => (
                            <div className="m-2 assessment-plan-suggestion-theme__order-reason" key={medication.value.value}>
                              <CFormTextarea placeholder={`Reason for ${medication.value.label}`} value={medication.reason || ''} onChange={(e) => handleReasonChange(suggestion.name, 'Medication', e.target.value, idx)} />
                            </div>
                          ))}
                        </div>
                        <div className="assessment-plan-suggestion-theme__order-section">
                          <CheckOptionSelections options={imagingOptions} selectedValue={selectedData[suggestion.name]?.Imaging?.map((item) => item.value) || []} setSelectedValue={(selectedOptions) => handleMultiSelectChange(suggestion.name, 'Imaging', selectedOptions)} Label={'Imaging'} />

                          {selectedData[suggestion.name]?.Imaging?.map((imaging, idx) => (
                            <div className="m-2 assessment-plan-suggestion-theme__order-reason" key={imaging.value.value}>
                              <CFormTextarea placeholder={`Reason for ${imaging.value.label}`} value={imaging.reason || ''} onChange={(e) => handleReasonChange(suggestion.name, 'Imaging', e.target.value, idx)} />
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    {activeTab === 'plan' && (
                      <div className="mt-3 assessment-plan-suggestion-theme__order-panel assessment-plan-suggestion-theme__plan-panel">
                        <PlanSuggestions
                          key={`${parentId}-${index}`}
                          parentIndex={index}
                          parentSuggestionId={parentId}
                          suggestionName={suggestion.name}
                          selectedDiagnosis={selectedDiagnosis}
                          onAccordionsUpdate={onHeaderPlanAccordionsChange || handleAccordionsUpdate}
                          planAccordions={onHeaderPlanAccordionsChange ? headerPlanSuggestions : suggestionsData?.find((item) => item.name === suggestion.name) || {}}
                          ApiCallingType={ApiCallingType}
                          handelSubmit={handelSubmit}

                          // planAccordions={
                          //     suggestionsData.find(item => item.name === suggestion) || [] // Match by name
                          // }
                        />
                      </div>
                    )}
                  </div>
                }
                index={index}
                parentId={parentId}
                setOpenSelectedAccordian={setOpenSelectedAccordian}
                open={true}
                isOpen={true}
                toggleAccordion={() => toggleAccordion(index)}
                information={information}
              />
            )
          }
        } else {
          return (
            <div className="mt-2">
              <AccordianFinal
                title={suggestion}
                index={index}
                parentId={parentId}
                showIcon={false}
                selectedAccordian={selectedAccordian}
                setSelectedAccordian={setSelectedAccordian}
                isSelected={openSelectedAccordian?.parentId === parentId && openSelectedAccordian?.index === index}
                isOpen={activeAccordion === index}
                toggleAccordion={() => toggleAccordion(index, parentId, suggestion.name)}
                setParentAccordions={setParentAccordions}
                information={information}
                setActiveDescriptions={setActiveDescriptions}
                handleAddStatementDescription={handleAddStatementDescription}
                handleFileStatementData={handleFileStatementData}
                handleStatementFileSave={handleStatementFileSave}
                file={file}
                setFile={setFile}
                fileStatmentData={fileStatmentData}
                attachedType={attachedType}
                differentModuleCopyFunctionality={differentModuleCopyFunctionality}
                handelSubmit={handelSubmit}
                onEditOption={onEditOption}
                deleteSuggestion={deleteSuggestion}
              />
            </div>
          )
        }
      })}
      {/* <div className="d-flex justify-content-end mt-2">
                <CButton onClick={() => handleSavePlanSuggestions()}>
                    submit
                </CButton>
            </div> */}
    </div>
  )
}

export default AccordianSuggestion
